import React from 'react';
import type { ResultStrategy, WidgetDefinition, EvaluatePanelPropsBase } from './ResultStrategy';
import type { Layouts } from 'react-grid-layout';
import { WidgetWrapper } from '../WidgetWrapper';
import { RollingCorrelationWidget } from '../RollingCorrelationWidget';
import { getColor } from '../../../../utils/colorUtils';

import { AiInsightWidget } from '../AiInsightWidget';

export class VarianceStrategy implements ResultStrategy {
    id = 'variance';

    match(nature?: string): boolean {
        if (!nature) return false;
        const n = nature.toLowerCase();
        return n === 'insight' || n === 'explain' || n === 'attribution' || n === 'variance';
    }

    getAvailableWidgets(hasAiReport: boolean): WidgetDefinition[] {
        const widgets: WidgetDefinition[] = [
            { id: 'attribution', label: '🔍 Attribution', icon: '🔍' },
            { id: 'insight', label: '💡 Insight', icon: '💡' },
            { id: 'rolling_corr', label: '📈 Rolling Correlation', icon: '📈' },
            { id: 'correlation_matrix', label: '🎛️ Correlation Matrix', icon: '🎛️' },
        ];
        if (hasAiReport) {
            widgets.push({ id: 'ai-insights', label: '🤖 AI Analysis', icon: '🤖' });
        }
        return widgets;
    }

    getDefaultWidgets(): string[] {
        return ['attribution', 'insight', 'rolling_corr', 'correlation_matrix'];
    }

    getLayout(activeWidgets: string[]): Layouts {
        return {
            lg: [
                { i: 'attribution', x: 0, y: 0, w: 2, h: 6 },
                { i: 'rolling_corr', x: 2, y: 0, w: 7, h: 6 },
                { i: 'correlation_matrix', x: 9, y: 0, w: 3, h: 6 },
                { i: 'insight', x: 0, y: 4, w: 2, h: 4 },
                { i: 'ai-insights', x: 4, y: 2, w: 4, h: 4 },
            ].filter(item => activeWidgets.includes(item.i))
        };
    }

    getStats(_data: any): React.ReactNode {
        return null; // Stats are embedded inside the widgets themselves
    }

    renderWidget(widgetId: string, props: EvaluatePanelPropsBase): React.ReactNode {
        // props.result for insight strategies is the raw backend dict
        const insight = props.result as unknown as {
            results: { label: string; score: number }[];
            explanation: string;
            target_label: string;
            row_count: number;
            rolling_correlations?: { label: string; points: { time: string; value: number }[] }[];
            correlation_matrix?: { x: string; y: string; value: number }[][];
        };

        switch (widgetId) {
            case 'attribution':
                return <AttributionWidget insight={insight} />;
            case 'insight':
                return <InsightTextWidget insight={insight} />;
            case 'rolling_corr':
                return <RollingCorrelationWidget series={insight?.rolling_correlations ?? []} />;
            case 'correlation_matrix':
                return <CorrelationMatrixWidget matrix={insight?.correlation_matrix ?? []} />;
            case 'ai-insights':
                if (!props.currentAiReport) return null;
                return <AiInsightWidget report={props.currentAiReport} />;
            default:
                return null;
        }
    }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AttributionWidget({ insight }: { insight: any }) {
    const results: { label: string; score: number }[] = insight?.results ?? [];
    const overallScore = insight?.overall_score ?? 0;
    const max = results.reduce((m, r) => Math.max(m, r.score), 0) || 1;

    // Map each result label to its index in rolling_correlations to get the SAME color as the chart
    const rollingCorrs = insight?.rolling_correlations ?? [];
    const getLabelColor = (label: string) => {
        const idx = rollingCorrs.findIndex((s: any) => s.label === label);
        return getColor(idx >= 0 ? idx : 0);
    };

    return (
        <WidgetWrapper title="🔍 Attribution">
            {results.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {insight?.explanation ?? 'No results.'}
                </p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', paddingTop: '0.5rem' }}>
                    {/* Overall Performance Section */}
                    <div style={{
                        padding: '0.75rem',
                        background: 'var(--surface-overlay-faint)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--surface-glass-border)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontWeight: 600, opacity: 0.9 }}>
                                Total Variance Explained (R²)
                            </span>
                            <span style={{ fontSize: '0.85rem', color: 'var(--accent-success)', fontWeight: 800 }}>
                                {(overallScore * 100).toFixed(1)}%
                            </span>
                        </div>
                        <div style={{
                            height: '6px',
                            borderRadius: '10px',
                            background: 'var(--surface-dark-soft)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${Math.min(100, Math.max(0, overallScore * 100))}%`,
                                background: 'linear-gradient(90deg, var(--accent-success) 0%, var(--nature-fundamental) 100%)',
                                borderRadius: '10px',
                                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            }} />
                        </div>
                        <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.4rem', fontStyle: 'italic' }}>
                            Higher % indicates stronger predictability.
                        </p>
                    </div>

                    <div style={{ padding: '0 0.5rem' }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.75rem' }}>
                            Relative Factor Weight
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                            {results.map((r) => {
                                const barColor = getLabelColor(r.label);
                                return (
                                    <div key={r.label}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                                                {r.label}
                                            </span>
                                            <span style={{ fontSize: '0.8rem', color: barColor, fontWeight: 700 }}>
                                                {(r.score * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                        <div style={{
                                            height: '10px',
                                            borderRadius: 'var(--radius-sm)',
                                            background: 'var(--surface-glass)',
                                            overflow: 'hidden',
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${(r.score / max) * 100}%`,
                                                background: barColor,
                                                borderRadius: 'var(--radius-sm)',
                                                transition: 'width 0.5s ease',
                                            }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem', opacity: 0.6 }}>
                        Based on {insight?.row_count ?? '?'} delta observations
                    </p>
                </div>
            )}
        </WidgetWrapper>
    );
}

function InsightTextWidget({ insight }: { insight: any }) {
    // Helper to render bold text from ** markers
    const renderExplanation = (text: string) => {
        if (!text) return '—';
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} style={{ color: 'var(--text-primary)' }}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };

    return (
        <WidgetWrapper title="💡 Explanation">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {insight?.target_label && (
                    <div>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target</span>
                        <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            {insight.target_label}
                        </p>
                    </div>
                )}
                <div>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Model Interpretation</span>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        {renderExplanation(insight?.explanation)}
                    </p>
                </div>
            </div>
        </WidgetWrapper>
    );
}

function CorrelationMatrixWidget({ matrix }: { matrix: any[][] }) {
    if (!matrix || matrix.length === 0) return (
        <WidgetWrapper title="🎛️ Correlation Matrix">
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No data (need ≥ 2 params).</p>
        </WidgetWrapper>
    );

    const labels = matrix.map(row => row[0].y);
    const size = labels.length;

    const getColor = (val: number) => {
        const alpha = Math.abs(val);
        // Use HSL for smoother transitions
        // Positive correlation: Green (140)
        // Negative correlation: Red (0)
        if (val >= 0) {
            return `rgba(var(--accent-success-rgb), ${alpha})`;
        } else {
            return `rgba(var(--accent-danger-rgb), ${alpha})`;
        }
    };

    return (
        <WidgetWrapper title="🎛️ Correlation Matrix">
            <div style={{
                display: 'grid',
                gridTemplateColumns: `minmax(40px, auto) repeat(${size}, 1fr)`,
                gap: '2px',
                padding: '0.5rem',
                fontSize: '0.6rem',
                maxHeight: '100%',
                overflow: 'auto'
            }}>
                {/* Header row */}
                <div></div>
                {labels.map(l => (
                    <div key={l} style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'var(--text-muted)',
                        transform: 'rotate(-45deg)',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'center'
                    }} title={l}>
                        {l}
                    </div>
                ))}

                {/* Data rows */}
                {matrix.map((row, i) => (
                    <React.Fragment key={i}>
                        <div style={{
                            fontWeight: 'bold',
                            textAlign: 'right',
                            paddingRight: '6px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end'
                        }} title={labels[i]}>
                            {labels[i]}
                        </div>
                        {row.map((cell, j) => (
                            <div
                                key={j}
                                style={{
                                    aspectRatio: '1/1',
                                    background: getColor(cell.value),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--text-primary)',
                                    borderRadius: '2px',
                                    border: '1px solid var(--surface-overlay-subtle)',
                                    cursor: 'help',
                                    fontWeight: i === j ? 800 : 400
                                }}
                                title={`${cell.y} vs ${cell.x}: ${cell.value.toFixed(3)}`}
                            >
                                {cell.value.toFixed(2)}
                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>
            <p style={{ fontSize: '0.55rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.5rem', opacity: 0.6, fontStyle: 'italic' }}>
                Pearson Correlation of Z-score Deltas
            </p>
        </WidgetWrapper>
    );
}
